<template>
  <div>
    <WishlistsButton
      v-if="isWishlistCollection"
      class="wishlists-button"
      :deal-id="deal.id"
      :for-collection="true"
    />
    <a
      class="stretched-link text-decoration-none"
      :href="`${deal.get_absolute_url}`"
      @click.exact.prevent="onClickTile()"
      @click.ctrl.prevent="onClickTile(true)"
    >
      &nbsp;
    </a>
    <div class="image-container">
      <img
        v-if="isCarousel"
        class="carousel-cell-image product-image"
        :alt="deal.public_name"
        :data-flickity-lazyload="`${deal.media_url}?width=450&height=300&aspect_ratio=3:2`"
        :src="lazyPlaceholderImage"
        :class="{ 'has-banner': hasBanner(deal),
                  'last-call-margin': isLimitedTimeSale(deal),
        }"
      >
      <v-lazy-image
        v-else
        class="product-image pin-image-top"
        :class="{ 'has-banner': hasBanner(deal),
                  'last-call-margin': isLimitedTimeSale(deal),
        }"
        :src="`${deal.media_url}?width=450&height=300&aspect_ratio=3:2`"
        :src-placeholder="lazyPlaceholderImage"
        :alt="deal.public_name"
      />
      <DealBanner
        class="product-card-deal-banner"
        :deal="deal"
        :is-black-friday-last-call="isBlackFridayLastCall(deal)"
        :is-last-call="isLastCall(deal)"
        :is-limited-time-sale="isLimitedTimeSale(deal)"
      />
      <SumoDayBadge v-if="isSumoDay(deal)" class="campaign-badge sumo-day-badge" />
      <BlackFridayBadge
        v-if="isBlackFriday(deal) || isBlackFridayLastCall(deal)"
        class="campaign-badge black-friday-badge"
      />
    </div>
    <div class="p-3 description-container">
      <p class="section-title d-block mt-3">
        {{ deal.public_name }}
      </p>
      <p class="d-block deal-plan-type my-2">
        {{ getPlanType(deal) }}
      </p>
      <p class="d-block deal-description my-0">
        {{ deal.card_description }}
      </p>
      <DealRating
        class="my-2"
        is-deal-card
        :deal="deal"
      />
      <div class="d-flex align-items-center p-0 m-0">
        <span class="d-inline-block w-100">
          <deal-price
            class="product-carousel-component"
            :price="deal.price"
            :original-price="deal.original_price"
            :is-free="deal.is_free"
          />
        </span>
        <a
          v-if="!deal.is_marketplace && !isOriginals(deal)"
          :href="`${deal.get_absolute_url}`"
          class="ribbon-container"
          @click.exact.prevent="onClickTile()"
          @click.ctrl.prevent="onClickTile(true)"
        >
          <div v-popover="{ name: `ribbon-${title}-${deal.id}`, position: 'top' }">
            <div class="ribbon">
              AppSumo Select
            </div>
          </div>
        </a>
        <client-only>
          <v-popover
            class="popover"
            event="hover"
            data-popover="select"
            ignore-top-alignment
            :width="300"
            :pointer="false"
            :name="`ribbon-${title}-${deal.id}`"
          >
            Exclusive offers on the hottest software, expertly tested,
            vetted, and brought to you by the AppSumo team.
          </v-popover>
        </client-only>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import VLazyImage from 'v-lazy-image';
import { DEAL_PLACEHOLDER_IMG } from '@/constants/global';
import SumoDayBadge from '~/static/sumoday-badge.svg?inline';
import BlackFridayBadge from '~/static/campaigns/black-friday-badge.svg?inline';
import { isWishlistCollectionSlug } from '~/util/global';
import DealBanner from '../utilities/DealBanner.vue';
import DealRating from '../utilities/DealRating.vue';
import WishlistsButton from '../wishlists/Button.vue';
import CampaignsMixin from '~/mixins/campaigns';
import DealPrice from '~/components/common/DealPrice.vue';

export default {
  components: {
    DealBanner,
    DealPrice,
    DealRating,
    VLazyImage,
    SumoDayBadge,
    BlackFridayBadge,
    WishlistsButton,
  },
  mixins: [CampaignsMixin],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    isCarousel: {
      type: Boolean,
    },
  },
  computed: {
    lazyPlaceholderImage() {
      return DEAL_PLACEHOLDER_IMG;
    },
    isWishlistCollection() {
      return isWishlistCollectionSlug(this.$route?.params.pathMatch);
    },
    wishlistData() {
      return this.$store.getters['wishlists/wishlist'];
    },

  },
  methods: {
    onClickTile(isNewWindow = false) {
      if (!this.isCarousel) {
        if (isNewWindow) {
          window.open(this.deal.get_absolute_url);
        } else {
          this.$router.push(this.deal.get_absolute_url);
        }
      }
    },
    getPlanType(deal) {
      let textStr = '';
      if (deal.unique_plan_types) {
        for (let i = 0; i < deal.unique_plan_types.length; i += 1) {
          const typeArr = deal.unique_plan_types[i];
          if (typeArr[0] === 'One Time Purchase') {
            textStr += 'Lifetime deal';
          } else if (typeArr[0] === 'Limited License') {
            textStr += `${typeArr[2]} ${typeArr[1]} DEAL`;
          } else {
            textStr += typeArr[0];
          }
          if (i < deal.unique_plan_types.length - 1) {
            textStr += ' & ';
          }
        }
      }
      return textStr;
    },
    generateCardDescription(deal) {
      return get(deal, 'card_description');
    },
    isOriginals(deal) {
      return (get(deal, 'internal_tags', []) || []).includes('originals');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~/styles/utils/breakpoints';

.deal-card-container {
  position: relative;
}

.campaign-badge {
  position: absolute;

  &.sumo-day-badge {
    width: 108px;
    height: 30px;
    top: 8px;
    right: 0;
  }

  &.black-friday-badge {
    width: 115px;
    height: 32px;
    top: 8px;
    right: 8px;
  }
}

.stretched-link {
  width: 100%;
  height: 100%;
  display: block;
  position: absolute;
  font-size: 0;
}

.product-card-deal-banner {
  border-radius: 0;
  width: 100%;
  position: initial;
}

.original-price {
  text-decoration: line-through;
  color: #cdcdcd;
  font-size: 14px;
  line-height: 21px;
}

.price {
  font-weight: 500;
  font-size: 20px;
  line-height: 32px;
}

.deal-plan-type {
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  text-transform: lowercase;
  color: #09292b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0;
}

.deal-plan-type::first-letter {
  text-transform: capitalize;
}

.deal-description {
  color: #434d59;
  font-size: 16px;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  /* stylelint-disable-next-line */
  display: -webkit-box !important;
  -webkit-line-clamp: 2;
  /* stylelint-disable-next-line */
  -webkit-box-orient: vertical;
  height: 48px;
}

.description-container {
  box-shadow: 0 0 20px rgba(1, 0, 0, 0.1);
  border: 1px solid #dadde5;
  border-top: 0;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
}

.image-container {
  width: 100%;
  height: auto;
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  position: relative;
  overflow: hidden;
  border: 1px solid #dadde5;
  border-bottom: 0;
  background-color: #fff;

  .has-banner {
    margin-top: -31px;
  }

  .last-call-margin {
    margin-top: -29px;
  }
}

.product-image {
  min-height: 180px;
  width: 100%;
}

.section-title {
  color: #021414;
  cursor: pointer;
  display: inline-block;
  font-size: 19px;
  font-weight: 600;
  line-height: 22px;
  max-width: 100%;
  overflow: hidden;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-title:hover,
.section-title:focus {
  color: #021414;
}

.pricing-container {
  display: inline-block;
}

.ribbon-container {
  &:hover {
    text-decoration: none;
    color: #fff;
  }
}

.ribbon {
  font-size: 14px;
  position: relative;
  background: #09292b;
  color: #fff;
  padding: 4px 8px 4px 6px;
  height: 28px;
  white-space: nowrap;
  line-height: 20px;
  right: 0;
  margin-right: -16px;
  z-index: 1;
  cursor: pointer;
}

.ribbon::before {
  content: '';
  position: absolute;
  display: block;
  border: 14px solid #09292b;
  left: -13px;
  top: 0;
  border-right-width: 0;
  border-left-color: transparent;
}

.wishlists-button {
  margin: 8px;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}

@include media-breakpoint-down(md) {
  .mobile-ribbon-filler {
    display: block !important;
    height: 22px;
    width: 100%;
  }
}

@include media-breakpoint-up(lg) {
  .product-image {
    min-height: 215px;
    width: 100%;
  }
}
</style>
